import React from "react";
import {
  Box,
  ButtonBase,
  Divider,
  IconButton,
  Typography,
  Paper,
  Popper,
  Tooltip,
} from "@mui/material";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { alpha } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";

import LOGO from "../../../assets/logo.webp";
import { routes } from "../../../routes";
import { COLORS, palette } from "../../../theme/theme-options";
import { SIDEBAR_COLLAPSED_WIDTH_PX, SIDEBAR_WIDTH_PX } from "../../../constants";

const isActive = (pathname: string, href?: string) => {
  if (!href) {
    return false;
  }
  if (href === "/") {
    return pathname === "/";
  }
  return pathname.startsWith(href);
};

const DesktopSideNav: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = React.useState<boolean>(() => {
    try {
      return window.localStorage.getItem("sidebar-collapsed") === "1";
    } catch {
      return false;
    }
  });
  const [openId, setOpenId] = React.useState<string | null>(null);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    try {
      window.localStorage.setItem("sidebar-collapsed", collapsed ? "1" : "0");
    } catch {
      return;
    }
  }, [collapsed]);

  const closeTimer = React.useRef<number | null>(null);

  React.useEffect(() => {
    return () => {
      if (closeTimer.current) {
        window.clearTimeout(closeTimer.current);
      }
    };
  }, []);

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
    }
    closeTimer.current = null;
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimer.current = window.setTimeout(() => {
      setOpenId(null);
      setAnchorEl(null);
    }, 180);
  };

  const openFlyout = (id: string, el: HTMLElement) => {
    clearCloseTimer();
    setOpenId(id);
    setAnchorEl(el);
  };

  const current = routes.find((n) => n.href === openId);
  const flyoutOpen = Boolean(openId && anchorEl && current?.subs?.length);
  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH_PX : SIDEBAR_WIDTH_PX;

  return (
    <Box
      sx={{
        width: sidebarWidth,
        height: "100vh",
        position: "sticky",
        textAlign: "left",
        top: 0,
        backgroundColor: palette.sidebar.bg,
        backdropFilter: "blur(16px)",
        borderRight: `1px solid ${palette.sidebar.border}`,
        display: "flex",
        flexDirection: "column",
        transition: "width 220ms cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <Box
        sx={{
          padding: collapsed ? "14px 8px 10px 8px" : "14px 12px 10px 12px",
          display: "flex",
          justifyContent: collapsed ? "center" : "flex-start",
        }}
      >
        <ButtonBase
          onClick={() => navigate("/dashboards")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.2,
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              background: `linear-gradient(135deg, ${alpha(COLORS.primary[500], 0.18)}, ${alpha(
                COLORS.primary[600],
                0.12,
              )})`,
              border: `1px solid ${alpha(COLORS.primary[400], 0.22)}`,
              overflow: "hidden",
            }}
          >
            <img src={LOGO} width={30} alt="Logo" />
          </Box>
          {!collapsed && (
            <Typography
              sx={{
                color: palette.sidebar.textStrong,
                fontWeight: 800,
                fontSize: 24,
                letterSpacing: 0.1,
                lineHeight: 1,
              }}
            >
              Pure IT
            </Typography>
          )}
        </ButtonBase>
      </Box>

      <Divider sx={{ borderColor: palette.sidebar.border }} />

      <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
        {routes.map((item) => {
          const active = isActive(pathname, item.href);
          const hasFlyout = Boolean(item.subs?.length);

          return (
            <Tooltip
              key={item.href + item.label}
              title={item.label}
              placement="right"
              disableHoverListener={!collapsed}
            >
              <ButtonBase
                onClick={() => navigate(item.href)}
                onMouseEnter={(e) => {
                  if (hasFlyout) {
                    openFlyout(item.href, e.currentTarget);
                  } else {
                    scheduleClose();
                  }
                }}
                onMouseLeave={() => {
                  if (hasFlyout) {
                    scheduleClose();
                  }
                }}
                sx={{ width: "100%", justifyContent: "left" }}
              >
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: collapsed ? "center" : "flex-start",
                    gap: collapsed ? 0 : 1,
                    width: "100%",
                    minHeight: 40,
                    padding: collapsed ? "8px 0px" : "8px 12px",
                    paddingLeft: collapsed ? 0 : "14px",
                    color: active ? palette.sidebar.textStrong : palette.sidebar.text,
                    backgroundColor: active
                      ? alpha(COLORS.primary[500], 0.12)
                      : "transparent",
                    fontWeight: 500,
                    transition: "all 160ms ease",
                    "&:hover": {
                      backgroundColor: alpha(COLORS.grey[800], 0.35),
                      color: palette.sidebar.textStrong,
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 3,
                      backgroundColor: active ? palette.sidebar.accent : "transparent",
                    }}
                  />
                  <Box sx={{ width: 22, display: "grid", placeItems: "center" }}>
                    {item.icon}
                  </Box>
                  {!collapsed && (
                    <Typography sx={{ fontSize: 13, fontWeight: active ? 700 : 600 }}>
                      {item.label}
                    </Typography>
                  )}
                </Box>
              </ButtonBase>
            </Tooltip>
          );
        })}
      </Box>

      <Box sx={{ flex: 1 }} />

      <Divider sx={{ borderColor: palette.sidebar.border }} />
      <Box sx={{ p: 1, display: "flex", justifyContent: "center" }}>
        <IconButton
          size="small"
          onClick={() => setCollapsed((prev) => !prev)}
          sx={{
            color: alpha(COLORS.grey[300], 0.85),
            border: `1px solid ${alpha(COLORS.grey[700], 0.7)}`,
            borderRadius: 2,
            width: 30,
            height: 30,
            "&:hover": {
              backgroundColor: alpha(COLORS.grey[800], 0.35),
              color: COLORS.grey[100],
            },
          }}
        >
          {collapsed ? (
            <ChevronRightRoundedIcon fontSize="small" />
          ) : (
            <ChevronLeftRoundedIcon fontSize="small" />
          )}
        </IconButton>
      </Box>

      <Popper
        open={flyoutOpen}
        anchorEl={anchorEl}
        placement="right-start"
        modifiers={[{ name: "offset", options: { offset: [8, 0] } }]}
        style={{ zIndex: 1300, pointerEvents: "none" }}
      >
        <Paper
          onMouseEnter={clearCloseTimer}
          onMouseLeave={scheduleClose}
          sx={{
            pointerEvents: "auto",
            width: 280,
            borderRadius: 2,
            backgroundColor: palette.sidebar.bg2,
            border: `1px solid ${palette.sidebar.border}`,
            boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
            overflow: "hidden",
          }}
        >
          <Box sx={{ padding: "10px 12px" }}>
            {current?.subs?.map((group) => (
              <Box key={group.main.label} sx={{ marginBottom: 1.25, width: "100%" }}>
                <ButtonBase
                  key={group.main.href}
                  onClick={() => navigate(group.main.href)}
                  sx={{
                    justifyContent: "start",
                    width: "100%",
                    padding: 1,
                    borderRadius: 1,
                    color: palette.sidebar.textStrong,
                    fontSize: 18,
                    fontWeight: 800,
                    marginBottom: 0.75,
                    "&:hover": {
                      backgroundColor: alpha(COLORS.primary[500], 0.16),
                    },
                  }}
                >
                  {group.main.label}
                  <ArrowRightAltIcon />
                </ButtonBase>

                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {group.children.map((it) => (
                    <ButtonBase
                      key={it.href}
                      onClick={() => navigate(it.href)}
                      sx={{
                        justifyContent: "start",
                        width: "100%",
                        borderRadius: 1,
                        padding: 1,
                        color: palette.sidebar.textStrong,
                        fontSize: 14,
                        fontWeight: 800,
                        "&:hover": {
                          backgroundColor: alpha(COLORS.primary[500], 0.16),
                          color: palette.sidebar.textStrong,
                        },
                      }}
                    >
                      {it.label}
                    </ButtonBase>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      </Popper>
    </Box>
  );
};

export default DesktopSideNav;
